import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { PickIndex } from "./chartGame.ts";

/*
 * Math Munch (ADR 0056, widened by 0098 §4), as pure functions over a state
 * value: a grid of squares, a rule, a muncher, and monsters that wander in. The
 * component feeds it key presses and animation-frame deltas; randomness comes
 * in through `pick`, so tests can fix it.
 *
 * The player chooses the operations. "Multiples of n" and "Factors of n" are
 * about multiplication, so they come up only when x or / is chosen; "Makes n"
 * works for any operation and its squares are facts from every chosen one. The
 * facts are enumerated (the fact families over 1 to 12), not drawn from the
 * generator, because a board needs every way of making n.
 */

export const COLS = 6;
export const ROWS = 5;
export const SQUARES = COLS * ROWS;
export const LIVES = 3;
export const MAX_FRAME_MS = 250;
/** Monsters stay away for this long after catching the muncher. */
export const GRACE_MS = 1500;
/** The "level complete" pause before the next rule. */
export const LEVEL_DONE_MS = 2000;
export const FIRST_MONSTER_MS = 4000;
export const NEXT_MONSTER_MS = 5000;
const START = 2 * COLS + 2;
const CORNERS = [0, COLS - 1, SQUARES - COLS, SQUARES - 1];

export type RuleKind = "multiples" | "makes" | "factors";
export type Direction = "up" | "down" | "left" | "right";

export interface Rule {
    kind: RuleKind;
    n: number;
}

export interface Square {
    /** The number shown, or the result of the fact for an `a op b` square. */
    value: number;
    /**
     * Set on "Makes n" squares, which show a fact rather than a number: its
     * operation (an `Operations` value) and the two numbers as printed, so
     * 15 - 3 has a = 15, b = 3 and value 12. A square with `a` and no `op` is a
     * multiplication, as they all were before ADR 0098.
     */
    op?: string;
    a?: number;
    b?: number;
    fits: boolean;
    eaten: boolean;
}

export interface Monster {
    id: number;
    pos: number;
    sinceStepMs: number;
}

export interface Mistake {
    rule: Rule;
    square: Square;
}

export type MunchEvent =
    | { kind: "munched"; seq: number; square: Square }
    | { kind: "wrong"; seq: number; square: Square }
    | { kind: "caught"; seq: number }
    | { kind: "level"; seq: number };

export interface MunchState {
    phase: "playing" | "levelDone" | "over";
    level: number;
    rule: Rule;
    squares: Square[];
    player: number;
    monsters: Monster[];
    calm: boolean;
    /** The operations the player chose; "Makes n" squares come from these. */
    operations: string[];
    lives: number;
    munched: number;
    wrong: number;
    mistakes: Mistake[];
    /** Play time in this level, for when monsters enter. */
    levelMs: number;
    monstersEntered: number;
    nextMonsterId: number;
    graceMs: number;
    levelDoneMs: number;
    lastEvent: MunchEvent | null;
}

/** What every game played before the player could choose (ADR 0098). */
export const MULTIPLICATION_ONLY: string[] = [Operations.Multiplication];

const KINDS: RuleKind[] = ["multiples", "makes", "factors"];
/** The tier rises every three levels, however many kinds there are. */
const TIER_EVERY = 3;
/** One list per tier. `makes` is the multiplication-only list. */
const TIERS: Record<RuleKind, number[][]> = {
    multiples: [[2, 5, 10], [3, 4], [6, 9], [7, 8], [11, 12]],
    makes: [[12, 24], [18, 36], [16, 20], [48, 60], [40, 72]],
    factors: [[12, 18], [20, 24], [30, 36], [40, 48], [60, 72]],
};
/** Targets once anything but multiplication is chosen; a tier is thinned to what the operations reach. */
const MAKES_TIERS = [[6, 8, 10], [9, 11, 12], [12, 14, 15], [16, 18, 20], [20, 24]];
/** A target is used only if the chosen operations make it this many different ways. */
const MIN_WAYS = 3;
/** Near misses of the non-multiplication operations lie within this of the target. */
const NEAR = 4;
/** Fewer misses than this and the trivial facts (x 1, / 1) are let back in. */
const MIN_MISSES = 6;

const ORDER = [Operations.Addition, Operations.Subtraction, Operations.Multiplication, Operations.Division] as string[];

/** An empty choice plays as multiplication, which is what the page opens on. */
function chosen(operations: string[]): string[] {
    return operations.length > 0 ? operations : MULTIPLICATION_ONLY;
}

/** Multiples and factors are multiplication ideas, so they need x or / (ADR 0098 §4). */
export function playsMultiples(operations: string[]): boolean {
    const ops = chosen(operations);
    return ops.includes(Operations.Multiplication) || ops.includes(Operations.Division);
}

/** A fact as the board shows it: `a op b`, whose result is `value`. */
export interface Fact {
    op: string;
    a: number;
    b: number;
    value: number;
}

function range(from: number, to: number): number[] {
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

function factOf(op: string, a: number, b: number): Fact {
    switch (op) {
        case Operations.Addition:
            return { op, a, b, value: a + b };
        case Operations.Subtraction:
            return { op, a: a + b, b, value: a };
        case Operations.Division:
            return { op, a: a * b, b, value: a };
        default:
            return { op, a, b, value: a * b };
    }
}

/** The fact families over 1 to 12 for each chosen operation: a + b, (a + b) - b, a x b, (a x b) / b. */
export function factsFor(operations: string[]): Fact[] {
    const ops = chosen(operations);
    const facts: Fact[] = [];
    for (const op of ORDER.filter((o) => ops.includes(o))) {
        for (const a of range(1, 12)) {
            for (const b of range(1, 12)) {
                facts.push(factOf(op, a, b));
            }
        }
    }
    return facts;
}

/** How many different facts make `n`; 3 + 4 and 4 + 3 are one way. */
export function waysToMake(n: number, operations: string[]): number {
    const commutes = (op: string) => op === Operations.Addition || op === Operations.Multiplication;
    return factsFor(operations).filter((f) => f.value === n && (!commutes(f.op) || f.a <= f.b)).length;
}

/**
 * The "Makes n" targets by tier. Multiplication alone keeps `multiplicationTiers`
 * exactly; otherwise each tier of MAKES_TIERS keeps only the targets the chosen
 * operations make enough ways, and a tier left empty (subtraction and division
 * only reach 12) repeats the tier before it.
 */
export function makesTiersFor(operations: string[], multiplicationTiers: number[][]): number[][] {
    const ops = chosen(operations);
    if (ops.every((op) => op === Operations.Multiplication)) {
        return multiplicationTiers;
    }
    const tiers: number[][] = [];
    for (const tier of MAKES_TIERS) {
        const reachable = tier.filter((n) => waysToMake(n, ops) >= MIN_WAYS);
        tiers.push(reachable.length > 0 ? reachable : tiers[tiers.length - 1] ?? MAKES_TIERS[0]);
    }
    return tiers;
}

export function ruleFor(level: number, pick: PickIndex, operations: string[] = MULTIPLICATION_ONLY): Rule {
    const kinds: RuleKind[] = playsMultiples(operations) ? KINDS : ["makes"];
    const kind = kinds[(level - 1) % kinds.length];
    const tiers = kind === "makes" ? makesTiersFor(operations, TIERS.makes) : TIERS[kind];
    const tier = tiers[Math.min(Math.floor((level - 1) / TIER_EVERY), tiers.length - 1)];
    return { kind, n: tier[pick(tier.length)] };
}

export function stepMsFor(level: number): number {
    return Math.max(700, 1400 * Math.pow(0.9, level - 1));
}

export function monstersFor(level: number, calm: boolean): number {
    if (calm) {
        return 0;
    }
    return level <= 2 ? 1 : 2;
}

export function fits(rule: Rule, square: Pick<Square, "value">): boolean {
    switch (rule.kind) {
        case "multiples":
            return square.value % rule.n === 0;
        case "factors":
            return rule.n % square.value === 0;
        case "makes":
            return square.value === rule.n;
    }
}

/** A fact that is not the target but sits where a wrong guess would (§2). */
function isNearMiss(fact: Fact, n: number): boolean {
    if (fact.value === n) {
        return false;
    }
    if (fact.op === Operations.Multiplication) {
        return fact.a >= 2 && fact.b >= 2 && Math.abs(fact.value - n) <= 12;
    }
    return Math.abs(fact.value - n) <= NEAR;
}

/** Dividing by 1 gives the number back, which teaches nothing as a distractor. */
function isTrivial(fact: Fact): boolean {
    return fact.op === Operations.Division && fact.b === 1;
}

/**
 * The squares that fit and the near misses that don't (§2). Misses sit where
 * a guess would: non-multiples inside the multiples' range, non-factors below
 * n, and, for "Makes n", facts of the chosen operations close to n - within 12
 * for multiplication (both numbers 2 or more), within 4 for the others.
 */
export function poolsFor(rule: Rule, operations: string[] = MULTIPLICATION_ONLY): { fit: Square[]; miss: Square[] } {
    const square = (value: number, extra: Partial<Square> = {}): Square => ({
        value,
        fits: false,
        eaten: false,
        ...extra,
    });
    const { n } = rule;
    if (rule.kind === "multiples") {
        const all = range(2, n * 10);
        return {
            fit: range(1, 10).map((k) => square(n * k, { fits: true })),
            miss: all.filter((v) => v % n !== 0).map((v) => square(v)),
        };
    }
    if (rule.kind === "factors") {
        return {
            fit: range(1, n).filter((v) => n % v === 0).map((v) => square(v, { fits: true })),
            miss: range(2, n).filter((v) => n % v !== 0).map((v) => square(v)),
        };
    }
    const facts = factsFor(operations);
    const asSquare = (f: Fact) => square(f.value, { op: f.op, a: f.a, b: f.b, fits: f.value === n });
    const near = facts.filter((f) => isNearMiss(f, n));
    const plain = near.filter((f) => !isTrivial(f));
    return {
        fit: facts.filter((f) => f.value === n).map(asSquare),
        miss: (plain.length >= MIN_MISSES ? plain : near).map(asSquare),
    };
}

function shuffle<T>(list: T[], pick: PickIndex): T[] {
    const out = [...list];
    for (let i = out.length - 1; i > 0; i--) {
        const j = pick(i + 1);
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

/** A fresh board: 10-12 squares that fit, the rest near misses, shuffled. */
export function boardFor(rule: Rule, pick: PickIndex, operations: string[] = MULTIPLICATION_ONLY): Square[] {
    const { fit, miss } = poolsFor(rule, operations);
    const fitCount = 10 + pick(3);
    const chosen: Square[] = [];
    for (let i = 0; i < SQUARES; i++) {
        const pool = i < fitCount ? fit : miss;
        chosen.push({ ...pool[pick(pool.length)] });
    }
    return shuffle(chosen, pick);
}

function levelState(base: Omit<MunchState, "rule" | "squares">, pick: PickIndex): MunchState {
    const rule = ruleFor(base.level, pick, base.operations);
    return { ...base, rule, squares: boardFor(rule, pick, base.operations) };
}

export function startMunch(calm: boolean, pick: PickIndex, operations: string[] = MULTIPLICATION_ONLY): MunchState {
    return levelState(
        {
            phase: "playing",
            level: 1,
            player: START,
            monsters: [],
            calm,
            operations: chosen(operations),
            lives: LIVES,
            munched: 0,
            wrong: 0,
            mistakes: [],
            levelMs: 0,
            monstersEntered: 0,
            nextMonsterId: 1,
            graceMs: 0,
            levelDoneMs: 0,
            lastEvent: null,
        },
        pick
    );
}

function nextSeq(state: MunchState): number {
    return (state.lastEvent?.seq ?? 0) + 1;
}

function rowOf(index: number): number {
    return Math.floor(index / COLS);
}

function colOf(index: number): number {
    return index % COLS;
}

export function stepFrom(index: number, dir: Direction): number | null {
    const r = rowOf(index);
    const c = colOf(index);
    if (dir === "up") return r > 0 ? index - COLS : null;
    if (dir === "down") return r < ROWS - 1 ? index + COLS : null;
    if (dir === "left") return c > 0 ? index - 1 : null;
    return c < COLS - 1 ? index + 1 : null;
}

/** The direction from one square to a neighbour, or null if they are not neighbours. */
export function directionTo(from: number, to: number): Direction | null {
    const dirs: Direction[] = ["up", "down", "left", "right"];
    return dirs.find((d) => stepFrom(from, d) === to) ?? null;
}

function distance(a: number, b: number): number {
    return Math.abs(rowOf(a) - rowOf(b)) + Math.abs(colOf(a) - colOf(b));
}

function farthestCorner(state: MunchState): number {
    const taken = new Set(state.monsters.map((m) => m.pos));
    return CORNERS.filter((c) => !taken.has(c)).reduce((best, c) =>
        distance(c, state.player) > distance(best, state.player) ? c : best
    );
}

/** Being caught: a life, a moment's grace, and the monster sent back to a far corner. */
function checkCaught(state: MunchState): MunchState {
    if (state.phase !== "playing" || state.graceMs > 0) {
        return state;
    }
    const catcher = state.monsters.find((m) => m.pos === state.player);
    if (!catcher) {
        return state;
    }
    const lives = state.lives - 1;
    const others = state.monsters.filter((m) => m.id !== catcher.id);
    const sentBack = { ...catcher, pos: farthestCorner({ ...state, monsters: others }), sinceStepMs: 0 };
    return {
        ...state,
        lives,
        phase: lives === 0 ? "over" : "playing",
        monsters: [...others, sentBack],
        graceMs: GRACE_MS,
        lastEvent: { kind: "caught", seq: nextSeq(state) },
    };
}

export function moveMuncher(state: MunchState, dir: Direction): MunchState {
    if (state.phase !== "playing") {
        return state;
    }
    const to = stepFrom(state.player, dir);
    if (to === null) {
        return state;
    }
    return checkCaught({ ...state, player: to });
}

/** Munch the square under the muncher (§1). An eaten square does nothing. */
export function munch(state: MunchState): MunchState {
    if (state.phase !== "playing") {
        return state;
    }
    const square = state.squares[state.player];
    if (square.eaten) {
        return state;
    }
    if (square.fits) {
        const squares = state.squares.map((s, i) => (i === state.player ? { ...s, eaten: true } : s));
        const next: MunchState = {
            ...state,
            squares,
            munched: state.munched + 1,
            lastEvent: { kind: "munched", seq: nextSeq(state), square },
        };
        if (squares.every((s) => !s.fits || s.eaten)) {
            return { ...next, phase: "levelDone", levelDoneMs: LEVEL_DONE_MS, monsters: [] };
        }
        return next;
    }
    const lives = state.lives - 1;
    return {
        ...state,
        lives,
        phase: lives === 0 ? "over" : "playing",
        wrong: state.wrong + 1,
        mistakes: [...state.mistakes, { rule: state.rule, square }],
        lastEvent: { kind: "wrong", seq: nextSeq(state), square },
    };
}

function stepMonster(state: MunchState, monster: Monster, pick: PickIndex): number {
    const options = (["up", "down", "left", "right"] as Direction[])
        .map((d) => stepFrom(monster.pos, d))
        .filter((p): p is number => p !== null);
    if (pick(2) === 0) {
        const closer = options.filter((p) => distance(p, state.player) < distance(monster.pos, state.player));
        if (closer.length > 0) {
            return closer[pick(closer.length)];
        }
    }
    return options[pick(options.length)];
}

/** Moves the game on by one frame of play time: monsters, grace, and the level-complete pause. */
export function tick(state: MunchState, frameMs: number, pick: PickIndex): MunchState {
    if (state.phase === "over") {
        return state;
    }
    const dt = Math.max(0, Math.min(frameMs, MAX_FRAME_MS));

    if (state.phase === "levelDone") {
        const left = state.levelDoneMs - dt;
        if (left > 0) {
            return { ...state, levelDoneMs: left };
        }
        return levelState(
            {
                ...state,
                phase: "playing",
                level: state.level + 1,
                player: START,
                monsters: [],
                levelMs: 0,
                monstersEntered: 0,
                graceMs: 0,
                levelDoneMs: 0,
                lastEvent: { kind: "level", seq: nextSeq(state) },
            },
            pick
        );
    }

    let next: MunchState = {
        ...state,
        levelMs: state.levelMs + dt,
        graceMs: Math.max(0, state.graceMs - dt),
    };

    const due = FIRST_MONSTER_MS + next.monstersEntered * NEXT_MONSTER_MS;
    if (next.monstersEntered < monstersFor(next.level, next.calm) && next.levelMs >= due) {
        next = {
            ...next,
            monsters: [...next.monsters, { id: next.nextMonsterId, pos: farthestCorner(next), sinceStepMs: 0 }],
            monstersEntered: next.monstersEntered + 1,
            nextMonsterId: next.nextMonsterId + 1,
        };
    }

    const stepMs = stepMsFor(next.level);
    next = {
        ...next,
        monsters: next.monsters.map((m) => {
            const since = m.sinceStepMs + dt;
            if (since < stepMs) {
                return { ...m, sinceStepMs: since };
            }
            return { ...m, pos: stepMonster(next, m, pick), sinceStepMs: since - stepMs };
        }),
    };
    return checkCaught(next);
}

/** What is left to eat on this board. */
export function remaining(state: MunchState): number {
    return state.squares.filter((s) => s.fits && !s.eaten).length;
}
